"use client";

import { CLink } from "@/components/common/customLink/CustomLink";
import Button from "@/components/ui/button/Button";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { BrandLogoSkeleton } from "./BrandLogoSkeleton";

interface BrandLogo {
  url: string;
  altText?: string;
}

interface CarouselBrand {
  _key: string;
  name?: string;
  logo?: BrandLogo;
  link?: { url?: string };
}

interface LogoCarouselSingleRowProps {
  brands: CarouselBrand[];
  title?: string;
  viewAllLabel?: string;
  /** Resolved URL for the "View All" button. Hidden when empty. */
  viewAllHref?: string;
  locale: string;
}

function getLayout(width: number): { cols: number; pageSize: number } {
  if (width >= 1024) return { cols: 6, pageSize: 6 };
  if (width >= 768) return { cols: 4, pageSize: 4 };
  return { cols: 2, pageSize: 2 };
}

function buildPages(brands: CarouselBrand[], pageSize: number): CarouselBrand[][] {
  const pages: CarouselBrand[][] = [];
  for (let i = 0; i < brands.length; i += pageSize) {
    const chunk = brands.slice(i, i + pageSize);
    // Pad so every slide is exactly pageSize items (keeps slide height consistent)
    while (chunk.length < pageSize) {
      chunk.push({ _key: `__pad-${i}-${chunk.length}` });
    }
    pages.push(chunk);
  }
  return pages;
}

const COLS_CLASS: Record<number, string> = {
  2: "grid-cols-2",
  4: "grid-cols-4",
  6: "grid-cols-6",
};

export default function LogoCarouselSingleRow({
  brands,
  title,
  viewAllLabel,
  viewAllHref = "",
  locale,
}: LogoCarouselSingleRowProps) {
  const isRTL = locale === "ar";
  const containerRef = useRef<HTMLDivElement>(null);

  const [cols, setCols] = useState(6);
  const [pageSize, setPageSize] = useState(6);

  useEffect(() => {
    const update = () => {
      const layout = getLayout(window.innerWidth);
      setCols(layout.cols);
      setPageSize(layout.pageSize);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
    containScroll: false,
    dragFree: false,
    direction: "ltr",
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setTimeout(() => onSelect(), 0);
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Re-init Embla when layout changes so slide count recalculates
  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, cols, pageSize]);

  const pages = buildPages(brands, pageSize);
  const gridColsClass = COLS_CLASS[cols] ?? "grid-cols-2";

  return (
    <div ref={containerRef} className="w-full max-w-7xl mx-auto">
      {/* Carousel area with arrows */}
      <div className="relative px-0 md:px-12">
        <div ref={emblaRef} className="overflow-hidden" dir="ltr">
          <div className="flex">
            {pages.map((pageBrands, pageIdx) => (
              <div key={pageIdx} className="flex-shrink-0 w-full min-w-0">
                <div className={`grid ${gridColsClass}`}>
                  {pageBrands.map((brand, idx) => (
                    <BrandLogoCell key={brand._key} brand={brand} priority={pageIdx === 0 && idx < pageSize} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Left Arrow */}
        <button
          onClick={() => (isRTL ? emblaApi?.scrollNext() : emblaApi?.scrollPrev())}
          disabled={isRTL ? !canScrollNext : !canScrollPrev}
          className="absolute left-2 top-1/2 -translate-y-1/2 p-1 disabled:opacity-30 hover:opacity-70 transition-opacity hidden md:block"
          aria-label="Previous"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Right Arrow */}
        <button
          onClick={() => (isRTL ? emblaApi?.scrollPrev() : emblaApi?.scrollNext())}
          disabled={isRTL ? !canScrollPrev : !canScrollNext}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 disabled:opacity-30 hover:opacity-70 transition-opacity hidden md:block"
          aria-label="Next"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* View All Button — only rendered when a CMS link is configured */}
      {viewAllHref && (
        <CLink
          href={viewAllHref}
          className="mx-auto flex w-full max-w-[10.625rem] items-center justify-center pt-7 md:pt-9"
        >
          <Button
            variant="contained"
            className="flex h-[2.5rem] w-full items-center justify-center text-sm font-semibold md:h-[3.125rem]"
          >
            {viewAllLabel || (locale === "ar" ? "عرض الكل" : "View All")}
          </Button>
        </CLink>
      )}
    </div>
  );
}

/* Individual brand logo cell */
function BrandLogoCell({
  brand,
  priority = false,
}: {
  brand: CarouselBrand;
  priority?: boolean;
}) {
  const logoSrc = brand.logo?.url ?? null;
  const [loaded, setLoaded] = useState(false);

  if (brand._key.startsWith("__pad")) {
    return <div className="h-20 md:h-24" />;
  }

  const content = (
    <div className="flex items-center justify-center p-4 h-20 md:h-24 select-none touch-pan-y">
      {logoSrc && (
        <div className="relative w-full h-full max-w-[120px]">
          {!loaded && (
            <BrandLogoSkeleton className="absolute inset-0 w-full h-full rounded-md" />
          )}
          <Image
            src={logoSrc}
            alt={brand.name || "Brand logo"}
            fill
            sizes="(max-width: 767px) 45vw, (max-width: 1023px) 25vw, 120px"
            className={`object-contain transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
            draggable={false}
            loading={priority ? undefined : "lazy"}
            priority={priority}
            onLoad={() => setLoaded(true)}
          />
        </div>
      )}
    </div>
  );

  if (brand.link?.url) {
    return (
      <CLink
        href={brand.link.url}
        className="block grayscale hover:grayscale-0 transition-all duration-300"
      >
        {content}
      </CLink>
    );
  }

  return (
    <div className="grayscale hover:grayscale-0 transition-all duration-300">
      {content}
    </div>
  );
}
